/**
 * Onboarding Hook - V10 avec TanStack Query
 *
 * ✅ TanStack Query pour gestion du cache et déduplication automatique
 * ✅ Support du paramètre `enabled` pour attendre que l'auth soit prête
 * ❌ Ne gère PAS l'authentification (c'est le rôle de OnboardingRoute)
 *
 * L'auth est gérée par OnboardingRoute AVANT que ce hook soit appelé.
 * Ce hook suppose que le token est déjà valide QUAND enabled=true.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { onboardingAPI, OnboardingStatus } from '../utils/onboardingAPI';
import { logger } from '../utils/logger';
import { useAuthStore } from '../stores/authStore';

interface UseOnboardingOptions {
  enabled?: boolean;
}

// Query keys pour invalidation cohérente
export const onboardingKeys = {
  all: ['onboarding'] as const,
  progress: () => [...onboardingKeys.all, 'progress'] as const,
};

/**
 * Hook useOnboarding - V10 avec TanStack Query
 * Gère automatiquement le cache, la déduplication et les refetch
 */
export const useOnboarding = (options: UseOnboardingOptions = {}) => {
  const { enabled = true } = options;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ✅ TanStack Query pour charger la progression
  const {
    data: status,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: onboardingKeys.progress(),
    queryFn: async (): Promise<OnboardingStatus> => {
      logger.log('🔄 useOnboarding: Chargement de la progression...');
      const progressData = await onboardingAPI.getProgress();

      logger.log('✅ useOnboarding: Progression chargée', {
        stepsCompleted: progressData.stepsCompleted,
        totalSteps: progressData.totalSteps,
        nextStep: progressData.nextStep,
        isCompleted: progressData.progress?.isCompleted,
      });

      return progressData;
    },
    enabled, // Ne lance que quand l'auth est prête
    staleTime: 30 * 1000, // 30 secondes - évite refetch inutiles
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // ✅ Mutation pour mettre à jour une étape
  const updateStepMutation = useMutation({
    mutationFn: async ({ step, completed }: { step: string; completed: boolean }) => {
      logger.log(`🔄 useOnboarding: Mise à jour étape ${step}:`, completed);
      await onboardingAPI.updateStep(step, completed);
      return { step, completed };
    },
    onSuccess: (_, { step, completed }) => {
      logger.log(`✅ Étape ${step} mise à jour:`, completed);
      // Invalider le cache pour forcer un refetch
      queryClient.invalidateQueries({ queryKey: onboardingKeys.progress() });
    },
    onError: (err: any, { step }) => {
      logger.error(`❌ Erreur mise à jour étape ${step}:`, err);
    },
  });

  // ✅ Mutation pour passer à l'étape suivante
  const nextStepMutation = useMutation({
    mutationFn: async () => {
      logger.log('🔄 useOnboarding: Passage à l\'étape suivante...');
      const newStatus = await onboardingAPI.nextStep();
      return newStatus;
    },
    onSuccess: (newStatus) => {
      logger.log('✅ Étape suivante:', newStatus);
      // Mettre à jour le cache directement avec la nouvelle valeur
      queryClient.setQueryData(onboardingKeys.progress(), newStatus);
    },
    onError: (err: any) => {
      logger.error('❌ Erreur étape suivante:', err);
    },
  });

  // ✅ Mutation pour terminer l'onboarding
  const completeMutation = useMutation({
    mutationFn: async () => {
      logger.log('🔄 useOnboarding: Finalisation onboarding...');
      await onboardingAPI.complete();
    },
    onSuccess: () => {
      logger.log('🎉 Onboarding terminé!');
      // Mettre à jour le cache IMMÉDIATEMENT pour éviter redirect loop
      queryClient.setQueryData(onboardingKeys.progress(), (old: OnboardingStatus | undefined) => {
        if (!old) return old;
        return {
          ...old,
          progress: old.progress ? { ...old.progress, isCompleted: true, completedAt: new Date() } : null,
          stepsCompleted: old.totalSteps,
          nextStep: null,
          completionPercentage: 100,
        };
      });
      // Mettre à jour l'état auth (needsCompany = false car company existe)
      useAuthStore.getState().setNeedsCompany(false);
      navigate('/dashboard');
    },
    onError: (err: any) => {
      logger.error('❌ Erreur finalisation onboarding:', err);
    },
  });

  // ✅ Mutation pour passer l'onboarding
  const skipMutation = useMutation({
    mutationFn: async () => {
      logger.log('🔄 useOnboarding: Skip onboarding...');
      await onboardingAPI.skip();
    },
    onSuccess: () => {
      logger.log('⏭️ Onboarding passé');
      // Mettre à jour le cache IMMÉDIATEMENT pour éviter redirect loop
      queryClient.setQueryData(onboardingKeys.progress(), (old: OnboardingStatus | undefined) => {
        if (!old) return old;
        return {
          ...old,
          progress: old.progress ? { ...old.progress, isCompleted: true, skippedAt: new Date() } : null,
          stepsCompleted: old.totalSteps,
          nextStep: null,
          completionPercentage: 100,
        };
      });
      // Mettre à jour l'état auth
      useAuthStore.getState().setNeedsCompany(false);
      navigate('/dashboard');
    },
    onError: (err: any) => {
      logger.error('❌ Erreur skip onboarding:', err);
    },
  });

  // Wrapper functions pour compatibilité avec l'ancienne API
  const updateStep = useCallback(async (step: string, completed: boolean) => {
    await updateStepMutation.mutateAsync({ step, completed });
  }, [updateStepMutation]);

  const nextStep = useCallback(async () => {
    await nextStepMutation.mutateAsync();
  }, [nextStepMutation]);

  const complete = useCallback(async () => {
    await completeMutation.mutateAsync();
  }, [completeMutation]);

  const skip = useCallback(async () => {
    await skipMutation.mutateAsync();
  }, [skipMutation]);

  const refresh = useCallback(async () => {
    logger.log('🔄 useOnboarding: Refresh demandé');
    await refetch();
  }, [refetch]);

  // Mapper nextStep (string) vers currentStep (number)
  const mapNextStepToIndex = (nextStepName: string | null | undefined): number => {
    if (!nextStepName) return 1;

    const stepMap: Record<string, number> = {
      company: 1,
      legal: 2,
      logo: 3,
      client: 4,
      product: 5,
      quote: 6,
    };

    return stepMap[nextStepName] || 1;
  };

  // Déterminer si l'onboarding est en cours de mutation
  const isMutating =
    updateStepMutation.isPending ||
    nextStepMutation.isPending ||
    completeMutation.isPending ||
    skipMutation.isPending;

  return {
    // État
    status,
    isLoading: isLoading || isMutating,
    error: error?.message || null,

    // Propriétés calculées
    isCompleted: status
      ? status.stepsCompleted === status.totalSteps && !status.nextStep
      : false,
    completionPercentage: status?.completionPercentage || 0,
    nextStepName: status?.nextStep,
    currentStep: mapNextStepToIndex(status?.nextStep),
    totalSteps: status?.totalSteps || 6,

    // Données brutes
    data: status,

    // Actions
    refresh,
    updateStep,
    nextStep,
    complete,
    skip,

    // Alias pour compatibilité
    handleNext: nextStep,
    handleBack: () => {},
    handleSkip: skip,
  };
};
